/**
 * Persists an active chat session id for Discover / non-plan flows.
 *
 * Problem this solves: when a user lands on /create via a Discover deep-link
 * (intent=discover, sourceType, sourceId) there's no planId tied to the chat.
 * `useChat` auto-generates a client-side id per mount. If the user navigates
 * away and returns, a NEW id is generated — the original messages are in
 * ai_messages but the client has lost track of which session to resume.
 *
 * Result: the chat "disappears." Reported 2026-04-23.
 *
 * Fix: pre-mint a UUID on the first Discover-flow mount, persist with a TTL,
 * reuse on return. The existing `useAiSessionMessages(sessionId, token)` hook
 * then hydrates the thread from ai_messages.
 *
 * Scope: session-scoped (sessionStorage, not localStorage) — if the user opens
 * a new tab or closes the browser, we want a fresh chat. TTL adds a safety net
 * for stale ids within the same browser session.
 */

import { registerWorkspaceReset } from '@/lib/workspace/workspaceResetRegistry';

const STORAGE_KEY = 'mora:chat:discover-session';
const TTL_MS = 60 * 60 * 1000; // 1 hour

interface StoredSession {
  id: string;
  at: number;
}

export function getOrCreateDiscoverSessionId(): string {
  if (typeof window === 'undefined') return crypto.randomUUID();
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as StoredSession;
      if (parsed?.id && typeof parsed.at === 'number' && Date.now() - parsed.at < TTL_MS) {
        return parsed.id;
      }
      // Stale — fall through to create a fresh one.
    }
  } catch {
    /* fall through */
  }
  const id = crypto.randomUUID();
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ id, at: Date.now() } satisfies StoredSession),
    );
  } catch {
    /* storage quota or disabled */
  }
  return id;
}

/** Touch the stored session to reset its TTL — call on each successful send. */
export function touchDiscoverSessionId(id: string): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ id, at: Date.now() } satisfies StoredSession),
    );
  } catch {
    /* noop */
  }
}

/** Read the stored discover session id without creating a new one. Returns null if absent or stale. */
export function peekDiscoverSessionId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    if (parsed?.id && typeof parsed.at === 'number' && Date.now() - parsed.at < TTL_MS) {
      return parsed.id;
    }
  } catch {
    /* fall through */
  }
  return null;
}

export function clearDiscoverSessionId(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

/**
 * A chat session belongs to the workspace it started in. Without this, Home
 * resumed the previous workspace's thread after a switch (found live
 * 2026-10-05: ITC Tax's chat answered "no Google Ads connected" inside Mora AI
 * Studio, which has Google Ads).
 */
registerWorkspaceReset('discover-chat-session', clearDiscoverSessionId);
